import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { withExperience } from '@helpcenter/analytics-components';
import { ExperienceName } from '@helpcenter/constants';
import type { RouterContext } from 'react-resource-router';
import type { PortalAnnouncement } from 'rest/portal';
import type { State } from 'state';
import { fetchRequestCreateAction } from 'state/actions/request-create';
import {
    getPortalAnnouncement,
    getName,
    getProjectId,
    getIsProjectSimplified,
    getCanAdministerProject,
} from 'state/selectors/portal';
import { getRequestCreateOrError, getFormName } from 'state/selectors/request-create';
import { getCanAdministerJIRA } from 'state/selectors/user';
import * as qs from '@atlassian/help-center-common-util/history-qs';
import RequestCreateDumb from './request-create-page';
import type { PersistedError } from 'state/persisted/types';

type ExternalProps = RouterContext;

type Props = ExternalProps & {
    fetchRequestCreate: typeof fetchRequestCreateAction;
    isLoading: boolean;
    error: PersistedError | undefined;
    announcement: PortalAnnouncement | undefined;
    projectId: number;
    portalName: string | undefined;
    requestTypeName: string | undefined;
    isProjectSimplified: boolean;
    isProjectAdmin: boolean;
};

interface RequestCreatePageState {
    sideBarOpen: boolean;
}

const toNumber = (idFromPath: string) => {
    return idFromPath ? Number(idFromPath) : undefined;
};

class RequestCreatePage extends React.Component<Props, RequestCreatePageState> {
    state = {
        sideBarOpen: !!qs.get('customize'),
    };

    toggleSideBar = (value?: boolean) => {
        this.setState({ sideBarOpen: value !== undefined ? value : !this.state.sideBarOpen });
    };

    componentDidMount() {
        const { requestTypeId, portalId } = this.props.match.params;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.props.fetchRequestCreate({ portalId: toNumber(portalId!)!, id: toNumber(requestTypeId!)! });
    }

    componentDidUpdate(prevProps: Props) {
        const { fetchRequestCreate } = this.props;
        const { portalId, requestTypeId } = this.props.match.params;
        const { requestTypeId: prevRequestId } = prevProps.match.params;

        const requestChanged = requestTypeId && requestTypeId !== prevRequestId;
        if (requestChanged) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            fetchRequestCreate({ portalId: toNumber(portalId!)!, id: toNumber(requestTypeId!)! });
        }
    }

    render() {
        const { sideBarOpen } = this.state;
        const {
            isLoading,
            error,
            announcement,
            requestTypeName,
            portalName,
            isProjectSimplified,
            isProjectAdmin,
            projectId,
        } = this.props;
        const { requestTypeId, portalId } = this.props.match.params;
        return (
            <RequestCreateDumb
                requestTypeName={requestTypeName}
                projectId={projectId}
                portalName={portalName}
                loading={isLoading}
                announcement={announcement}
                requestTypeId={Number(requestTypeId)}
                portalId={Number(portalId)}
                sideBarOpen={sideBarOpen}
                toggleSidebar={this.toggleSideBar}
                isProjectSimplified={isProjectSimplified}
                isProjectAdmin={isProjectAdmin}
                {...error}
            />
        );
    }
}

const selector = createSelector(
    (state: State, props: ExternalProps) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        getRequestCreateOrError(state, props.match.params.portalId!, props.match.params.requestTypeId!),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (state: State, props: ExternalProps) => getPortalAnnouncement(state, toNumber(props.match.params.portalId!)),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (state: State, props: ExternalProps) => getProjectId(state, toNumber(props.match.params.portalId!)),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (state: State, props: ExternalProps) => getName(state, toNumber(props.match.params.portalId!)),
    (state: State, props: ExternalProps) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        getFormName(state, toNumber(props.match.params.portalId!), toNumber(props.match.params.requestTypeId!)),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (state: State, props: ExternalProps) => getIsProjectSimplified(state, toNumber(props.match.params.portalId!)),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (state: State, props: ExternalProps) => getCanAdministerProject(state, toNumber(props.match.params.portalId!)),

    (state: State) => getCanAdministerJIRA(state),
    (
        request,
        announcement,
        projectId,
        portalName,
        requestTypeName,
        isProjectSimplified,
        isProjectAdmin,
        isJiraAdmin
    ) => ({
        announcement,
        projectId,
        portalName,
        requestTypeName,
        isProjectSimplified,
        isProjectAdmin,
        isJiraAdmin,
        error: request && 'error' in request ? request : undefined,
        isLoading: !request,
    })
);

export default connect(selector, {
    fetchRequestCreate: fetchRequestCreateAction,
})(withExperience(ExperienceName.REQUEST_CREATE)(RequestCreatePage));
